import { Phone, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { motion } from 'framer-motion';

export const Hero = ({ onBookClick }) => {
  const handleBookAppointment = () => {
    if (onBookClick) {
      onBookClick();
    } else {
      window.location.href = 'tel:+919986671810';
    }
  };

  const scrollToTreatments = () => {
    const element = document.getElementById('treatments');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, rgba(45, 74, 62, 0.05) 0%, rgba(249, 248, 246, 1) 100%)'
      }}
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1566792505656-e82d93abac30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxheXVydmVkaWMlMjBoZXJicyUyMHNwaWNlcyUyMHdvb2RlbiUyMHRhYmxlfGVufDB8fHx8MTc3MDIyNjAxNHww&ixlib=rb-4.1.0&q=85"
          alt="Ayurvedic herbs and spices"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F9F8F6]/60 via-[#F9F8F6]/80 to-[#F9F8F6]" />
      </div>

      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-left"
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="caption inline-block mb-4"
            >
              Welcome to AyurvedMed Healthcare
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="heading-1 text-[#2D4A3E] mb-6"
              data-testid="hero-heading"
            >
              Authentic Ayurveda for{' '}
              <span className="italic text-[#C67D63]">Holistic Healing</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="body-large text-[#6B6B6B] mb-8 max-w-lg"
              data-testid="hero-description"
            >
              Experience the ancient wisdom of Ayurveda with personalized treatments
              that address the root cause of ailments, not just symptoms. Begin your
              journey to complete wellness today.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                onClick={handleBookAppointment}
                data-testid="hero-book-btn"
                className="bg-[#2D4A3E] hover:bg-[#1e3329] text-white px-8 py-4 rounded-full flex items-center justify-center gap-2 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <Phone className="w-5 h-5" />
                Book Appointment
              </Button>

              <Button
                onClick={scrollToTreatments}
                data-testid="hero-explore-btn"
                variant="outline"
                className="border-2 border-[#2D4A3E] text-[#2D4A3E] hover:bg-[#2D4A3E] hover:text-white px-8 py-4 rounded-full flex items-center justify-center gap-2 text-lg font-medium transition-all duration-300"
              >
                Explore Treatments
                <ArrowRight className="w-5 h-5" />
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex gap-12 mt-12 pt-8 border-t border-[#E6E2D8]"
            >
              <div>
                <p className="text-3xl font-semibold text-[#2D4A3E]">5+</p>
                <p className="text-sm text-[#6B6B6B]">Years Experience</p>
              </div>
              <div>
                <p className="text-3xl font-semibold text-[#2D4A3E]">5,000+</p>
                <p className="text-sm text-[#6B6B6B]">Happy Patients</p>
              </div>
              <div>
                <p className="text-3xl font-semibold text-[#2D4A3E]">50+</p>
                <p className="text-sm text-[#6B6B6B]">Treatments</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1653508311259-692e4ddadaab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxheXVydmVkYSUyMGRvY3RvciUyMGNvbnN1bHRhdGlvbiUyMHB1bHNlJTIwY2hlY2t8ZW58MHx8fHwxNzcwMjI2MDE1fDA&ixlib=rb-4.1.0&q=85"
                alt="Ayurvedic consultation"
                className="w-full h-[500px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2D4A3E]/20 to-transparent" />
            </div>

            {/* Floating Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-[#E6E2D8]"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#C67D63]/10 flex items-center justify-center">
                  <span className="text-2xl">🌿</span>
                </div>
                <div>
                  <p className="font-semibold text-[#2D4A3E]">100% Natural</p>
                  <p className="text-sm text-[#6B6B6B]">Herbal Treatments</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
